import React, { useEffect, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import AppButton from "../button/component";
import History from "../../assets/footer-b/history.svg";
import HistoryGrey from "../../assets/footer-b/history-grey.svg";
import Invite from "../../assets/footer-b/invite.svg";
import InviteGrey from "../../assets/footer-b/invite-grey.svg";
import Awards from "../../assets/footer-b/awards.svg";
import AwardsGrey from "../../assets/footer-b/awards-grey.svg";
import Support from "../../assets/footer-b/support.svg";
import SupportGrey from "../../assets/footer-b/support-grey.svg";

/* FOOTER COMPONENT */

const FooterV2 = ({
  className = "",
  buttonTitle = "",
  onClick = () => {},
  disabled,
  buttonIcon,
  buttonId,
  showError,
  errorMessage,
  onInviteClick,
  onPlusClick = () => {},
  onPlusClose = () => {},
}) => {
  const { pathname } = useLocation();
  const { locale } = useParams();
  const { t } = useTranslation();

  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);

  const [activeTab, setActiveTab] = useState(null);

  useEffect(() => {
    const resize = () => setIsMobile(window.innerWidth < 1024);
    window.addEventListener("resize", resize);
    return () => window.removeEventListener("resize", resize);
  }, []);

  // Hide Olark button on mobile when footer-b is present
  useEffect(() => {
    if (isMobile) {
      const hideOlarkButton = () => {
        const buttonSelectors = [
          ".olark-launch-button-wrapper",
          ".olark-launch-button",
          '[id*="olark-launch"]',
        ];

        buttonSelectors.forEach((selector) => {
          const elements = document.querySelectorAll(selector);
          elements.forEach((element) => {
            if (element) {
              element.style.setProperty("display", "none", "important");
            }
          });
        });
      };

      // Hide immediately and on a delay to catch late-loading Olark
      hideOlarkButton();
      const timer = setTimeout(hideOlarkButton, 1000);

      return () => clearTimeout(timer);
    }
  }, [isMobile]);

  const currentLocale = locale ? `/${locale}` : "";

  const handleSupportClick = () => {
    onPlusClose();
    setActiveTab("support");
    // Only handle clicks on mobile devices
    if (window.innerWidth >= 1024) {
      return; // Let desktop use the default Olark button
    }

    if (window.olark) {
      // Show only the Olark chat box on mobile
      const chatBoxSelectors = ["#olark-box", '[id*="olark-box"]'];

      chatBoxSelectors.forEach((selector) => {
        const elements = document.querySelectorAll(selector);
        elements.forEach((element) => {
          if (element) {
            element.style.setProperty("display", "block", "important");
            element.style.setProperty("position", "fixed", "important");
            element.style.setProperty("z-index", "9999999", "important");

            // Mobile positioning only
            element.style.setProperty("bottom", "120px", "important");
            element.style.setProperty("right", "10px", "important");
            element.style.setProperty("left", "10px", "important");
            element.style.setProperty(
              "width",
              "calc(100vw - 20px)",
              "important",
            );
            element.style.setProperty("max-width", "400px", "important");
            element.style.setProperty("max-height", "500px", "important");
          }
        });
      });

      // Add custom CSS for mobile chat styling
      if (!document.getElementById("custom-olark-mobile-chat-styles")) {
        const style = document.createElement("style");
        style.id = "custom-olark-mobile-chat-styles";
        style.innerHTML = `
          @media (max-width: 1023px) {
            #olark-box {
              box-shadow: 0 4px 20px rgba(0,0,0,0.15) !important;
              border-radius: 12px !important;
              overflow: hidden !important;
              margin: 0 auto !important;
            }
          }
        `;
        document.head.appendChild(style);
      }

      window.olark("api.box.show");
      window.olark("api.box.expand");
    }
  };
  const closeOlarkChat = () => {
    if (window.olark) {
      window.olark("api.box.shrink");
      window.olark("api.box.hide");
    }
  };

  /* ===========================
     MOBILE FOOTER
  =========================== */

  if (isMobile) {
    return (
      <footer className={`w-full bg-surface-card py-[24px] ${className}`}>
        <div className="flex flex-col items-center justify-center text-center px-3">
          <div className="flex items-center justify-center gap-4 text-[13px] sm:text-[14px]">
            <Link
              to={`https://insightfultraits.com${currentLocale}/privacy-policy`}
              target="_blank"
              className="text-insightful-dark-orange underline underline-offset-2 hover:opacity-80 transition-opacity"
            >
              {t("footer.privacy_policy")}
            </Link>

            <Link
              to={`https://insightfultraits.com${currentLocale}/terms-conditions`}
              target="_blank"
              className="text-insightful-dark-orange underline underline-offset-2 hover:opacity-80 transition-opacity"
            >
              {t("footer.terms_and_conditions")}
            </Link>

            <Link
              to={`https://insightfultraits.com${currentLocale}/contact`}
              target="_blank"
              className="text-insightful-dark-orange underline underline-offset-2 hover:opacity-80 transition-opacity"
            >
              {t("footer.contact_us")}
            </Link>
          </div>

          <p className="mt-3 text-[13px] sm:text-[14px] text-insightful-gray">
            © {new Date().getFullYear()} {t("footer.insightfultraits_llc")}
          </p>
        </div>
      </footer>
    );
  }

  /* ===========================
     WEB FOOTER (UNCHANGED)
  =========================== */

  return (
    <footer className={`bg-surface-card py-[28px] sm:py-[30px] ${className}`}>
      <div className="flex flex-col items-center justify-center text-center">
        <div className="flex items-center justify-center gap-5 text-[15px] sm:text-[16px]">
          <Link
            to={`https://insightfultraits.com${currentLocale}/privacy-policy`}
            target="_blank"
            className="text-insightful-dark-orange underline underline-offset-2 hover:opacity-80 transition-opacity"
          >
            {t("footer.privacy_policy")}
          </Link>

          <Link
            to={`https://insightfultraits.com${currentLocale}/terms-conditions`}
            target="_blank"
            className="text-insightful-dark-orange underline underline-offset-2 hover:opacity-80 transition-opacity"
          >
            {t("footer.terms_and_conditions")}
          </Link>

          <Link
            to={`https://insightfultraits.com${currentLocale}/contact`}
            target="_blank"
            className="text-insightful-dark-orange underline underline-offset-2 hover:opacity-80 transition-opacity"
          >
            {t("footer.contact_us")}
          </Link>
        </div>

        <p className="mt-3 text-[14px] sm:text-[15px] text-insightful-gray">
          © {new Date().getFullYear()} {t("footer.insightfultraits_llc")}
        </p>
      </div>
    </footer>
  );
};

export default FooterV2;
